
import Vue from "vue";
import { Component, Inject, Watch } from "vue-property-decorator";
import { configStore, documentStore, uiStore } from "@/store/store";
import {
  BuilderElement,
  BuilderElementProjectManagementValue
} from "@/types/elements";
import AddElement from "@/components/Add/AddElement.vue";
import Accordion from "@/components/UI/Accordion.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import SearchInput from "@/components/Reusable/CustomInputs/SearchInput.vue";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import WebsiteDialog from "@/components/Add/WebsiteDialog.vue";
import { keyboardShortcuts } from "@/lib/keyboard-shortcuts/keyboard-shortcuts";
import {
  getFuzzySearchedElements,
  isElementAvailable,
  isElementHidden,
  setElementHtmlTag
} from "@/lib/elements/utils";
import { PanelContext } from "@/components/Builder/SidebarPanels.vue";
import { forceNextTick } from "@/util/force-next-tick";
import { closeUiPanel } from "@/lib/panels/panels";

@Component({
  components: {
    SearchInput,
    TextInput,
    Accordion,
    AddElement,
    DropdownInput,
    WebsiteDialog
  }
})
export default class ElementsList extends Vue {
  openCategoriesIndex = [0, 1];
  searchString = "";
  selectedSearchElementIndex = 0;

  @Inject({ from: "panelContext", default: {} })
  readonly panelContext?: PanelContext;

  get builderMode() {
    return configStore.builderMode;
  }

  addSelectedSearchItem() {
    const slug = this.elementsForSearch[this.selectedSearchElementIndex]?.slug;

    if (
      !slug ||
      !isElementAvailable(
        this.elementsForSearch[this.selectedSearchElementIndex]
      )
    ) {
      return;
    }

    documentStore.addElement({
      elementSlug: slug
    });

    if (this.doesSearchQueryMatchAnyHtmlTag) {
      setElementHtmlTag(
        uiStore.activeElementId!,
        this.searchString,
        `DocumentAddElement:Slug:${slug}`
      );
    }

    if (this.builderMode === "oxygen") {
      closeUiPanel("add");
    }
  }

  get searchResultsCount() {
    return Object.keys(this.elementsForSearch).length;
  }

  goToSelectedSearchElement(direction = "next") {
    let nextIndex =
      direction === "next"
        ? this.selectedSearchElementIndex + 1
        : this.selectedSearchElementIndex - 1;

    if (nextIndex === this.searchResultsCount) {
      nextIndex = 0;
    } else if (-1 === nextIndex) {
      nextIndex = this.searchResultsCount - 1;
    }

    this.selectedSearchElementIndex = nextIndex;
    this.scrollToActive();
  }

  nextOrPreviousSelectedSearchElement(event: KeyboardEvent) {
    if (this.searchString !== "") {
      event.preventDefault();
    }

    this.goToSelectedSearchElement(event.shiftKey ? "prev" : "next");
  }

  handleSearch(newSearch: string) {
    // keeping the scroll when all elements changed due to search is disorienting
    if (this.searchString !== newSearch) {
      document.querySelector(".breakdance-add-panel")?.scrollTo({ top: 0 });
    }

    this.searchString = newSearch;
  }

  get isAddPanelOpen() {
    return this.panelContext?.state === "add";
  }

  @Watch("isAddPanelOpen")
  resetSearchWhenPanelOpens() {
    if (this.isAddPanelOpen) {
      this.handleSearch("");
    }
  }

  resetSelectedSearchItemIndex(event: KeyboardEvent) {
    const ignoreKeys = [
      "Enter",
      "Tab",
      "ShiftLeft",
      "ShiftRight",
      "ArrowUp",
      "ArrowDown"
    ];

    if (!ignoreKeys.includes(event.code)) {
      this.selectedSearchElementIndex = 0;
    }
  }

  get elementsCount() {
    return this.elements.length;
  }

  chooseCategory(newIndex: number) {
    this.searchString = "";

    const existingCategoryIndex = this.openCategoriesIndex.findIndex(
      i => i === newIndex
    );

    if (existingCategoryIndex === -1) {
      this.openCategoriesIndex = [...this.openCategoriesIndex, newIndex];
    } else {
      this.openCategoriesIndex.splice(existingCategoryIndex, 1);
    }
  }

  elementsInActiveCategory(categorySlug: string) {
    return this.elements.filter(element => element.category === categorySlug);
  }

  get elementCategories() {
    return configStore.builderElementCategories;
  }

  get elements(): BuilderElement[] {
    return (Object.values(configStore.elements) as BuilderElement[])
      .filter(element =>
        uiStore.settings.showExperimentalElements
          ? true
          : element.experimental !== true
      )
      .filter(
        element => !isElementHidden(element.addPanelRules, element.settings)
      )
      .filter(element => isElementAvailable(element))
      .sort((a, b) => {
        if (a.order < b.order) return -1;
        if (a.order > b.order) return 1;
        return 0;
      });
  }

  get searchInHtmlTags() {
    return configStore.builderMode === "oxygen";
  }

  get doesSearchQueryMatchAnyHtmlTag() {
    return this.filteredSearchElements.find(item => {
      return [item.htmlTag?.default, ...item.htmlTag?.options]
        .filter(Boolean)
        .find(option => option === this.searchString);
    });
  }

  get elementsForSearch(): BuilderElement[] {
    if (this.doesSearchQueryMatchAnyHtmlTag) {
      return this.filteredSearchElements.sort((a, b) => {
        const catA = a.category?.toLowerCase();
        const catB = b.category?.toLowerCase();
        if (catA === catB) return 0;
        if (catA === "basic") return -1;
        if (catB === "basic") return 1;
        return 0;
      });
    }

    return this.filteredSearchElements;
  }

  get filteredSearchElements(): BuilderElement[] {
    const extraKeys = this.searchInHtmlTags
      ? ["htmlTag.default", "htmlTag.options"]
      : [];

    const results = getFuzzySearchedElements(
      this.elements,
      this.searchString,
      extraKeys
    );

    const resultsAfterInternalSearchTool = results.filter(e => {
      const pm = e.projectManagement ? e.projectManagement : undefined;

      return !Object.values({
        looksGood: this.advancedSearchLooksGood
          ? pm?.looksGood == this.advancedSearchLooksGood
          : undefined,
        optionsGood: this.advancedSearchOptionsGood
          ? pm?.optionsGood == this.advancedSearchOptionsGood
          : undefined,
        optionsWork: this.advancedSearchOptionsWork
          ? pm?.optionsWork == this.advancedSearchOptionsWork
          : undefined,
        dynamicBehaviorWorks: this.advancedSearchDynamicBehaviorWorks
          ? pm?.dynamicBehaviorWorks == this.advancedSearchDynamicBehaviorWorks
          : undefined,
        hasDynamicStuff: this.advancedSearchHasDynamicStuff
          ? e.dependencies || e.actions
          : undefined
      }).includes(false);
    });

    return resultsAfterInternalSearchTool;
  }

  scrollToActive() {
    forceNextTick(() => {
      document
        .querySelector(".breakdance-add-panel__element--selected")
        ?.scrollIntoView({
          block: "center"
        });
    });
  }

  dumpSearchResults() {
    console.log(this.elementsForSearch.map(e => e.name).join("\n"));
  }

  get advancedSearchDropdownItems(): BuilderElementProjectManagementValue[] {
    return ["yes", "no", "unknown", "sort of"];
  }

  advancedSearchAvailable = false;

  mounted() {
    keyboardShortcuts.register(
      "a d v",
      () => (this.advancedSearchAvailable = true)
    );
  }

  beforeDestroy() {
    keyboardShortcuts.unbind("a d v");
  }

  advancedSearchDialogOpen = false;
  advancedSearchHasDynamicStuff = false;
  advancedSearchLooksGood: BuilderElementProjectManagementValue | false = false;
  advancedSearchOptionsGood: BuilderElementProjectManagementValue | false =
    false;
  advancedSearchOptionsWork: BuilderElementProjectManagementValue | false =
    false;
  advancedSearchDynamicBehaviorWorks:
    | BuilderElementProjectManagementValue
    | false = false;

  get hasSearchParams() {
    return !!(
      this.searchString ||
      this.advancedSearchLooksGood ||
      this.advancedSearchOptionsGood ||
      this.advancedSearchOptionsWork ||
      this.advancedSearchDynamicBehaviorWorks ||
      this.advancedSearchHasDynamicStuff
    );
  }

  updateAdvancedSearchValue(key: string, value: unknown) {
    // @ts-ignore
    this[key] = value;
  }

  get isElementStudioOpen() {
    return uiStore.panelState.elementStudioOpen;
  }

  closePanel() {
    closeUiPanel("add");
  }
}
